import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight, BarChart3 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { KPICard, CustomTooltip, StatementHeader, SectionRow } from './ReportHelpers';

export function CashFlowReport({ reportData, viewMode, entityName }) {
  if (!reportData || !reportData.operating) return null;
  const { operating, investing, financing, net_change } = reportData;

  if (viewMode === 'list') {
    return (
      <Card data-testid="cf-list-view">
        <CardContent className="py-8 px-6 max-w-3xl mx-auto">
          <StatementHeader entityName={entityName} title="Statement of Cash Flows" period={reportData.period} />
          <table className="w-full text-sm">
            <tbody>
              <SectionRow label="CASH FLOWS FROM OPERATING ACTIVITIES" bold />
              {operating.items.map(item => <SectionRow key={item.name} label={item.name} amount={item.amount} indent />)}
              <SectionRow label="Net Cash from Operating Activities" amount={operating.total} total />
              <tr><td className="py-3" colSpan="2"></td></tr>
              <SectionRow label="CASH FLOWS FROM INVESTING ACTIVITIES" bold />
              {investing.items.length === 0 && <SectionRow label="No activity" indent />}
              {investing.items.map(item => <SectionRow key={item.name} label={item.name} amount={item.amount} indent />)}
              <SectionRow label="Net Cash from Investing Activities" amount={investing.total} total />
              <tr><td className="py-3" colSpan="2"></td></tr>
              <SectionRow label="CASH FLOWS FROM FINANCING ACTIVITIES" bold />
              {financing.items.length === 0 && <SectionRow label="No activity" indent />}
              {financing.items.map(item => <SectionRow key={item.name} label={item.name} amount={item.amount} indent />)}
              <SectionRow label="Net Cash from Financing Activities" amount={financing.total} total />
              <tr><td className="py-3" colSpan="2"></td></tr>
              <SectionRow label="NET CHANGE IN CASH" amount={net_change} grandTotal />
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  }

  const summaryData = [
    { name: 'Operating', amount: operating.total },
    { name: 'Investing', amount: investing.total },
    { name: 'Financing', amount: financing.total },
  ];

  const renderSection = (title, data) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm uppercase tracking-wider text-zinc-500">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.items.length === 0 ? (
          <p className="text-sm text-zinc-400 py-3">No activity</p>
        ) : (
          <div className="space-y-2">
            {data.items.map(item => (
              <div key={item.name} className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-0">
                <div className="flex items-center gap-2">
                  {item.amount >= 0 ? <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" /> : <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />}
                  <span className="text-sm text-zinc-700">{item.name}</span>
                </div>
                <span className={`font-mono text-sm font-medium ${item.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 pt-3 border-t-2 border-zinc-200 flex justify-between">
          <span className="font-semibold text-sm">Subtotal</span>
          <span className={`font-mono font-semibold ${data.total >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatCurrency(data.total)}</span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard title="Operating" value={formatCurrency(operating.total)} icon={TrendingUp} color={operating.total >= 0 ? 'green' : 'red'} />
        <KPICard title="Investing" value={formatCurrency(investing.total)} icon={BarChart3} color={investing.total >= 0 ? 'green' : 'red'} />
        <KPICard title="Financing" value={formatCurrency(financing.total)} icon={DollarSign} color={financing.total >= 0 ? 'green' : 'red'} />
        <KPICard title="Net Change" value={formatCurrency(net_change)} icon={net_change >= 0 ? TrendingUp : TrendingDown} color={net_change >= 0 ? 'green' : 'red'} />
      </div>

      {summaryData.some(d => d.amount !== 0) && (
        <Card>
          <CardHeader><CardTitle className="text-base">Cash Flow Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summaryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" name="Amount" radius={[6, 6, 0, 0]}>
                    {summaryData.map(entry => (
                      <Cell key={entry.name} fill={entry.amount >= 0 ? '#34D399' : '#F87171'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderSection('Operating Activities', operating)}
        {renderSection('Investing Activities', investing)}
        {renderSection('Financing Activities', financing)}
      </div>

      <div className={`p-5 rounded-xl ${net_change >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex justify-between items-center">
          <span className="font-semibold text-zinc-900">Net Cash Change</span>
          <span className={`text-3xl font-mono font-bold ${net_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(net_change)}</span>
        </div>
      </div>
    </div>
  );
}
